import { useState } from "react";

import { Button, Grid, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { SurveyConfiguration } from "@rubi/api-generated";

import ReportIcon from "@/common/ReportIcon";

const ReportButton = ({
  report,
  selectedReport,
  onReportSelect,
  disabled = false,
}: {
  report: SurveyConfiguration;
  selectedReport: SurveyConfiguration | null;
  onReportSelect: (report: SurveyConfiguration) => void;
  disabled?: boolean;
}) => {
  const isSelected = selectedReport?.name === report.name;
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Button
        variant={isSelected ? "contained" : "outlined"}
        color="primary"
        fullWidth
        disabled={disabled}
        onClick={() => !disabled && onReportSelect(report)}
        startIcon={
          <ReportIcon
            src={report.description.image_url}
            alt={report.name}
            disabled={disabled}
          />
        }
        sx={{
          justifyContent: "flex-start",
          textAlign: "left",
          height: "100%",
          padding: 1,
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? "primary.main" : "divider",
          backgroundColor: isSelected ? "primary.light" : "background.paper",
          color: isSelected ? "primary.contrastText" : "text.primary",
          "&:hover": {
            backgroundColor: isSelected ? "primary.main" : "action.hover",
          },
          ...(disabled && {
            backgroundColor: "action.disabledBackground",
            color: "action.disabled",
            borderColor: "action.disabled",
            "&:hover": {
              backgroundColor: "action.disabledBackground",
            },
          }),
        }}
      >
        {report.name}
      </Button>
    </Grid>
  );
};

const ReportFilterButtons = ({
  reports,
  selectedReport,
  onReportSelect,
  citizenEngageDisableHiddenContactNumber = "",
  citizenEngageDisableReportsContactNumber = "",
}: {
  reports: SurveyConfiguration[];
  selectedReport: SurveyConfiguration | null;
  onReportSelect: (report: SurveyConfiguration) => void;
  citizenEngageDisableHiddenContactNumber: string;
  citizenEngageDisableReportsContactNumber: string;
}) => {
  const [filterText, setFilterText] = useState("");

  const handleFilterChange = (event) => {
    setFilterText(event.target.value.toLowerCase());
  };

  const filteredReports = reports.filter((report) =>
    report.name.toLowerCase().includes(filterText),
  );

  const publicReports = filteredReports.filter((report) => !report.hidden);
  const hiddenReports = filteredReports.filter((report) => report.hidden);

  const allReportsDisabled = Boolean(citizenEngageDisableReportsContactNumber);

  return (
    <Box>
      {allReportsDisabled && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          These report types are disabled for sending referral links. Should you
          need access, please contact {citizenEngageDisableReportsContactNumber}
        </Typography>
      )}
      <TextField
        variant="outlined"
        label="Filter Reports"
        fullWidth
        margin="dense"
        value={filterText}
        onChange={handleFilterChange}
        style={{ marginBottom: "8px" }}
      />
      <Grid container spacing={1}>
        {publicReports.map((report) => (
          <ReportButton
            key={report.id}
            report={report}
            selectedReport={selectedReport}
            onReportSelect={onReportSelect}
            disabled={allReportsDisabled}
          />
        ))}
      </Grid>
      {hiddenReports.length > 0 && (
        <>
          <Typography
            variant="h6"
            color="textSecondary"
            style={{ marginTop: "16px", marginBottom: "8px" }}
          >
            Hidden / Partner Reports
          </Typography>
          {citizenEngageDisableHiddenContactNumber && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              These report types are disabled for sending referral links. Should
              you need access, please contact{" "}
              {citizenEngageDisableHiddenContactNumber}
            </Typography>
          )}
          <Grid container spacing={1}>
            {hiddenReports.map((report) => (
              <ReportButton
                key={report.id}
                report={report}
                selectedReport={selectedReport}
                onReportSelect={onReportSelect}
                disabled={
                  allReportsDisabled ||
                  Boolean(citizenEngageDisableHiddenContactNumber)
                }
              />
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ReportFilterButtons;
